import { useContext } from "react";
import axios, { AxiosRequestConfig, HttpStatusCode } from "axios";
import AuthContext from "common/store/AuthContext";

import { genApiDatePath, generateErrorMsg, getServerUrl } from "utils/commonTools";
import { createFileNameDate, toJapaneseDate } from "utils/DateTools";
import { useGetPrint } from "./useGetPrint";
import { getPrintResult } from "../types/getPrintResult";
import { PRINT_TYPE } from "../types/PRINT_TYPE";

//--------------------------------------
// 公園日報の印刷
//--------------------------------------
export const useDailyReport = () => {
  const authCtxt = useContext(AuthContext);
  const { token } = authCtxt;

  const { initiateDownload } = useGetPrint();

  const getDailyReort = async (
    date: Date,
    printType: number,
    title: string
  ): Promise<getPrintResult> => {
    const config = {
      headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
      responseType: "blob",
    } as AxiosRequestConfig;

    const parkId = authCtxt.selectedPark?.parkId;
    const parkName = authCtxt.selectedPark?.parkName;
    const target = printType === PRINT_TYPE.WORK_REPORT ? "work" : "daily";

    try {
      const response = await axios.get(
        getServerUrl() + `print/${target}/${parkId}/${genApiDatePath(date)}`,
        config
      );
      if (response.status === HttpStatusCode.NoContent) {
        return {
          succeeded: false,
          msg: "レポートを生成できませんでした（日報記入がまだ、または帳票が未登録です）",
        };
      }
      const url = window.URL.createObjectURL(response.data);

      const fileName = `${title}_${parkName}_${createFileNameDate(date)}.xlsx`;
      initiateDownload(url, fileName);

      return { succeeded: true, msg: "" };
    } catch (error) {
      var errMsg = generateErrorMsg(error);
      return { succeeded: false, msg: errMsg };
    }
  };

  const getRangeReorts = async (
    startDate: Date,
    endDate: Date,
    reportType: number,
    title: string
  ): Promise<getPrintResult> => {
    const config = {
      headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
      responseType: "blob",
      params: {
        from: toJapaneseDate(startDate),
        to: toJapaneseDate(endDate),
      },
    } as AxiosRequestConfig;

    const parkId = authCtxt.selectedPark?.parkId;
    const parkName = authCtxt.selectedPark?.parkName;

    try {
      const response = await axios.get(
        getServerUrl() + `print/daily/ranges/${parkId}/${reportType}`,
        config
      );
      if (response.status === HttpStatusCode.NoContent) {
        return {
          succeeded: false,
          msg: "レポートを生成できませんでした（日報記入がまだ、または帳票が未登録です）",
        };
      }
      const url = window.URL.createObjectURL(response.data);

      const fileName = `${title}_${parkName}_${createFileNameDate(startDate)}_${createFileNameDate(
        endDate
      )}.zip`;
      initiateDownload(url, fileName);

      return { succeeded: true, msg: "" };
    } catch (error) {
      var errMsg = generateErrorMsg(error);
      return { succeeded: false, msg: errMsg };
    }
  };

  return { getDailyReort, getRangeReorts };
};
